/**
 * Input field renderers — one component per field type, dispatched by
 * `FieldView`. Each is controlled: it receives the current value + an
 * `onChange` and never holds its own state.
 */
import { useRef, useState } from "preact/hooks";
import type { Field, Option, SingleSelectField } from "../types";
import type { FieldValue } from "../logic/validation";
import { iconSvg } from "./icons";
import { stateName, zipToState } from "../state/zip_state";

/**
 * Format a phone-input value as the user types: strips non-digits, optionally
 * drops a leading 1 (paste of "+1 281 771 6049"), and renders as a partial
 * "(XXX) XXX-XXXX" mask. Tolerates fewer than 10 digits so the user sees the
 * mask grow as they type rather than waiting until 10 digits are entered.
 */
function formatPhoneInput(raw: string): string {
  let digits = raw.replace(/\D/g, "");
  if (digits.length === 11 && digits[0] === "1") digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Full US state names — the value stored is the full name (GHL `state` accepts it). */
const US_STATE_NAMES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "District of Columbia", "Florida", "Georgia",
  "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
  "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota",
  "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island",
  "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont",
  "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming",
];

/**
 * Type-ahead state field — the user types a few letters, the list filters, they
 * click (or press Enter). Replaces a 50-item dropdown scroll. The committed
 * value is the full state name; it is set only by selecting a real state, so a
 * required `state` field cannot be satisfied by free text.
 */
function StateCombobox({
  value,
  error,
  onChange,
}: { value: FieldValue; error?: string } & { onChange: (v: FieldValue) => void }) {
  const committed = typeof value === "string" ? value : "";
  const [query, setQuery] = useState(committed);
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<number | undefined>(undefined);

  const q = query.trim().toLowerCase();
  const matches = q
    ? US_STATE_NAMES.filter((s) => s.toLowerCase().includes(q))
    : US_STATE_NAMES;

  function pick(name: string) {
    setQuery(name);
    setOpen(false);
    onChange(name);
  }

  return (
    <div class="fe-combobox">
      <input
        class={`fe-input${error ? " fe-input--error" : ""}`}
        type="text"
        role="combobox"
        aria-expanded={open}
        autocomplete="off"
        placeholder="Type your state…"
        value={query}
        onFocus={() => setOpen(true)}
        onInput={(e) => {
          setQuery((e.currentTarget as HTMLInputElement).value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && matches.length > 0) {
            e.preventDefault();
            pick(matches[0]!);
          }
        }}
        onBlur={() => {
          blurTimer.current = window.setTimeout(() => {
            setOpen(false);
            // Commit an exact (case-insensitive) typed match; else revert.
            const exact = US_STATE_NAMES.find(
              (s) => s.toLowerCase() === query.trim().toLowerCase(),
            );
            if (exact) {
              setQuery(exact);
              onChange(exact);
            } else {
              setQuery(committed);
            }
          }, 150);
        }}
      />
      {open && matches.length > 0 ? (
        <ul class="fe-combobox-list">
          {matches.map((s) => (
            <li
              class={`fe-combobox-option${s === committed ? " fe-combobox-option--active" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault(); // beat the input blur
                if (blurTimer.current) window.clearTimeout(blurTimer.current);
                pick(s);
              }}
            >
              {s}
            </li>
          ))}
        </ul>
      ) : null}
      {open && matches.length === 0 ? (
        <ul class="fe-combobox-list">
          <li class="fe-combobox-empty">No matching state</li>
        </ul>
      ) : null}
    </div>
  );
}

export interface FieldViewProps {
  field: Field;
  value: FieldValue;
  error?: string;
  onChange: (value: FieldValue) => void;
  /** Called when a text-family input loses focus — drives remote validation
   *  of `email` / `tel` fields flagged `remote_validate`. */
  onBlur?: () => void;
}

function ChoiceButton({
  option,
  selected,
  tile,
  onPick,
}: {
  option: Option;
  selected: boolean;
  tile: boolean;
  onPick: () => void;
}) {
  const iconMarkup = option.icon ? iconSvg(option.icon, tile ? 34 : 26) : null;
  return (
    <button
      type="button"
      class={`fe-choice${tile ? " fe-tile" : ""}${selected ? " fe-choice--selected" : ""}`}
      aria-pressed={selected}
      onClick={onPick}
    >
      {option.image_url ? (
        <img class="fe-tile-img" src={option.image_url} alt={option.label} />
      ) : iconMarkup ? (
        <span class="fe-choice-badge" dangerouslySetInnerHTML={{ __html: iconMarkup }} />
      ) : option.icon ? (
        <span class="fe-choice-icon">{option.icon}</span>
      ) : null}
      <span class="fe-choice-label">{option.label}</span>
    </button>
  );
}

function SingleSelect({ field, value, onChange }: { field: SingleSelectField } & Omit<FieldViewProps, "field">) {
  const display = field.display ?? "buttons";
  if (display === "dropdown") {
    return (
      <select
        class="fe-select"
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange((e.currentTarget as HTMLSelectElement).value)}
      >
        <option value="">Select…</option>
        {field.options.map((o) => (
          <option value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  return (
    <div class={`fe-choices${display === "tiles" ? " fe-choices--tiles" : ""}`}>
      {field.options.map((o) => (
        <ChoiceButton
          option={o}
          tile={display === "tiles"}
          selected={value === o.value}
          onPick={() => onChange(o.value)}
        />
      ))}
    </div>
  );
}

export function FieldView({ field, value, error, onChange, onBlur }: FieldViewProps) {
  const errCls = error ? " fe-input--error" : "";

  function control() {
    switch (field.type) {
      case "single_select":
        return <SingleSelect field={field} value={value} error={error} onChange={onChange} />;

      case "multi_select": {
        const arr = Array.isArray(value) ? value : [];
        return (
          <div class="fe-choices">
            {field.options.map((o) => {
              const selected = arr.includes(o.value);
              return (
                <ChoiceButton
                  option={o}
                  tile={false}
                  selected={selected}
                  onPick={() =>
                    onChange(
                      selected ? arr.filter((v) => v !== o.value) : [...arr, o.value],
                    )
                  }
                />
              );
            })}
          </div>
        );
      }

      case "tel": {
        // Live US/NANP phone formatter — strips non-digits as user types,
        // re-renders as "(XXX) XXX-XXXX". Tolerates 11-digit input with
        // leading 1 (paste of "+1 281 771 6049" works). Backend's
        // normalizeNanp() strips non-digits anyway, so the formatted string
        // is fully accepted server-side. Added 2026-05-24 per Glen UX
        // directive: "i like format in line".
        return (
          <input
            class={`fe-input${errCls}`}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder={"placeholder" in field ? field.placeholder : undefined}
            maxLength={14}
            value={typeof value === "string" ? value : ""}
            onInput={(e) => onChange(formatPhoneInput((e.currentTarget as HTMLInputElement).value))}
            onBlur={onBlur}
          />
        );
      }

      case "text":
      case "email":
      case "zip": {
        const inputMode =
          field.type === "email" ? "email" : field.type === "zip" ? "numeric" : "text";
        // WHATWG autofill tokens so mobile browsers can offer saved-value
        // suggestions (faster form completion on touch keyboards). Inferred
        // from field.id for text fields since we don't have a separate
        // semantic-purpose attribute on the field schema.
        const autoComplete =
          field.type === "email"
            ? "email"
            : field.type === "zip"
              ? "postal-code"
              : /first.?name|given.?name/i.test(field.id)
                ? "given-name"
                : /last.?name|family.?name|surname/i.test(field.id)
                  ? "family-name"
                  : undefined;
        return (
          <input
            class={`fe-input${errCls}`}
            type={field.type === "email" ? "email" : "text"}
            inputMode={inputMode}
            autoComplete={autoComplete}
            placeholder={"placeholder" in field ? field.placeholder : undefined}
            maxLength={field.type === "zip" ? 5 : field.type === "text" ? field.max_length : undefined}
            value={typeof value === "string" ? value : ""}
            onInput={(e) => onChange((e.currentTarget as HTMLInputElement).value)}
            onBlur={onBlur}
          />
        );
      }

      case "number":
        return (
          <input
            class={`fe-input${errCls}`}
            type="number"
            inputMode="decimal"
            min={field.min}
            max={field.max}
            step={field.step}
            value={typeof value === "number" ? value : ""}
            onInput={(e) => {
              const v = (e.currentTarget as HTMLInputElement).value;
              onChange(v === "" ? undefined : Number(v));
            }}
          />
        );

      case "date":
        return (
          <input
            class={`fe-input${errCls}`}
            type="date"
            min={field.min}
            max={field.max}
            value={typeof value === "string" ? value : ""}
            onInput={(e) => onChange((e.currentTarget as HTMLInputElement).value)}
          />
        );

      case "textarea":
        return (
          <textarea
            class={`fe-textarea${errCls}`}
            rows={field.rows ?? 4}
            maxLength={field.max_length}
            placeholder={field.placeholder}
            value={typeof value === "string" ? value : ""}
            onInput={(e) => onChange((e.currentTarget as HTMLTextAreaElement).value)}
          />
        );

      case "state":
        return <StateCombobox value={value} error={error} onChange={onChange} />;

      case "consent_checkbox":
        // TrustedForm Tagged Consent attribute spec (per ActiveProspect docs
        // 2026-05-24). Two roles on this field:
        //   - consent-opt-in : the checkbox the user interacts with
        //   - consent-language : the disclosure text element
        // The wrapping <div role="offer"> lives on the parent step (StepRenderer)
        // so TrustedForm can pair the language with the offer scope.
        return (
          <label class="fe-consent">
            <input
              type="checkbox"
              data-tf-element-role="consent-opt-in"
              checked={value === true}
              onChange={(e) => onChange((e.currentTarget as HTMLInputElement).checked)}
            />
            <span
              class="fe-consent-text"
              data-tf-element-role="consent-language"
              dangerouslySetInnerHTML={{ __html: field.consent_html }}
            />
          </label>
        );

      case "hidden":
        return null;

      default: {
        const _never: never = field;
        return <span>{`unknown field: ${JSON.stringify(_never)}`}</span>;
      }
    }
  }

  if (field.type === "hidden") return null;

  // Live ZIP → state derivation. When the user enters 5 digits, derive + show
  // the state inline so they get instant feedback ("ZIP 10001 — New York, NY")
  // and can see that their answer mapped to the right place. Suppressed if a
  // server error is already showing on the field — no need to stack messages.
  let zipStateHint: { ok: boolean; text: string } | null = null;
  if (field.type === "zip" && typeof value === "string" && !error) {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length === 5) {
      const code = zipToState(cleaned);
      if (code) {
        const name = stateName(code);
        zipStateHint = { ok: true, text: `✓ ${name} (${code})` };
      } else {
        zipStateHint = {
          ok: false,
          text: "We couldn't identify a US state for this ZIP. Please double-check.",
        };
      }
    }
  }

  return (
    <div class="fe-field">
      {field.label ? <label class="fe-label">{field.label}</label> : null}
      {field.help ? <span class="fe-help">{field.help}</span> : null}
      {control()}
      {zipStateHint ? (
        <span class={zipStateHint.ok ? "fe-zip-hint" : "fe-error"}>{zipStateHint.text}</span>
      ) : null}
      {error ? <span class="fe-error">{error}</span> : null}
    </div>
  );
}
